import React from "react";
import { FieldArray, FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import { CancelAdvancePaymentFormValue } from "src/models/AdvancePayment.model";
import usePaymentModes from "src/hooks/usePaymentModes";
import { handleValidNumber2 } from "src/utils/formUtils/ValidNumber";

type Props = {
  formikProps: FormikProps<CancelAdvancePaymentFormValue>;
  onClose: () => void;
  formType: "ADD" | "EDIT";
};

const CancelAdvancePayment = ({ formikProps, onClose, formType }: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;
  const { paymentMode: allModes } = usePaymentModes();

  const virtualModes = allModes?.filter((pm: any) => pm.type === "VIRTUAL") || [];

  return (
    <div className="p-4 flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="text-xl font-medium">
          {formType === "ADD" ? "Cancel Advance Payment" : "Update Payment"}
        </div>
        <button type="button" className="hover:bg-hover" onClick={onClose}>
          Cancel
        </button>
      </div>

      <ATMTextField
        name="cancelReason"
        value={values.cancelReason}
        required
        placeholder="Enter cancel reason"
        onChange={(e) => setFieldValue("cancelReason", e.target.value)}
        label="Cancel Reason"
      />

      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-300">
          <thead className="bg-gray-100 text-left">
            <tr>
              <th className="border px-4 py-2">Note</th>
              <th className="border px-4 py-2">Quantity</th>
              <th className="border px-4 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            <FieldArray name="paymentMode">
              {() =>
                values.paymentMode
                  .filter((item) => item.type === "PHYSICAL")
                  .map((note, index) => {
                    const availableQty = Number(note.availableQuantity || 0);
                    const isDisabled = availableQty === 0;
                    return (
                      <tr key={index}>
                        <td className="border px-4 py-2">{note.name} X</td>
                        <td className="border px-4 py-2">
                          <ATMTextField
                            name={`paymentMode[${index}].quantity`}
                            value={note.quantity}
                            disabled={isDisabled}
                            onChange={(e) =>
                              handleValidNumber2(e) &&
                              setFieldValue(
                                `paymentMode[${index}].quantity`,
                                e.target.value
                              )
                            }
                            onBlur={handleBlur}
                            placeholder="Qty"
                          />
                          <div className="text-xs mt-1 text-right">
                            {isDisabled ? (
                              <span className="text-red-500">Not available</span>
                            ) : (
                              `Available: ${availableQty}`
                            )}
                          </div>
                        </td>
                        <td className="border px-4 py-2">
                          {note.value * Number(note.quantity || 0)}
                        </td>
                      </tr>
                    );
                  })
              }
            </FieldArray>
          </tbody>
        </table>
      </div>

      {virtualModes.length > 0 && (
        <div className="border rounded-md p-2">
          <table className="w-full border text-left">
            <thead>
              <tr className="bg-gray-100 text-sm">
                <th className="p-2">Virtual Mode</th>
                <th className="p-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {virtualModes.map((mode, i) => {
                const existing = values.paymentMode.find(
                  (item) => item.name === mode.name
                );
                const virtualValue = existing?.value || "";

                return (
                  <tr key={i}>
                    <td className="p-2">{mode.name}</td>
                    <td className="p-2">
                      <ATMTextField
                        name={`virtualAmount_${mode.name}`}
                        value={virtualValue.toString()}
                        onChange={(e) => {
                          const amount = e.target.value;
                          if (handleValidNumber2({ target: { value: amount } })) {
                            const otherModes = values.paymentMode.filter(
                              (item) => item.name !== mode.name
                            );

                            if (amount) {
                              otherModes.push({
                                name: mode.name,
                                value: Number(amount),
                                quantity: 1,
                                type: "VIRTUAL",
                              });
                            }

                            setFieldValue("paymentMode", otherModes);
                          }
                        }}
                        placeholder="Amount"
                        onBlur={handleBlur}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ATMLoadingButton type="submit" isLoading={isSubmitting}>
        Save
      </ATMLoadingButton>
    </div>
  );
};

export default CancelAdvancePayment;
